import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import type { LeaderboardEntry, MeasurementPreferences } from '@kangaroo/shared';
import {
  Box,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { PageHeader, SectionCard } from '@kangaroo/ui';

import { CircuitTrackPreview } from '../../components/circuits/CircuitTrackPreview';
import {
  DETAILED_CIRCUIT_DEFAULT_LAYERS,
  DetailedCircuitMap,
} from '../../components/dev/TrackCatalogMap';
import { CardGridSkeleton } from '../../components/loading/PageSkeletons';
import { GpsSpeedTrackMap } from '../../components/telemetry/GpsSpeedTrackMap';
import { fetchLeaderboard, leaderboardKey } from '../../features/leaderboard';
import {
  circuitPreferencesKey,
  fetchCircuitPreferences,
  fetchTelemetryTracks,
  telemetryTracksKey,
  updateCircuitPreferences,
} from '../../features/telemetryContext';
import { useMeasurementPreferences } from '../../hooks/useMeasurementPreferences';
import {
  buildCircuitCatalogItems,
  buildCircuitTrackGroups,
  filterAndSortCircuitTrackGroups,
  formatCircuitDirection,
  selectMostUsedCircuitTrackGroups,
  sortCircuitTrackGroupsByDistance,
  type CircuitBrowseItem,
  type CircuitTrackGroup,
  type CircuitTrackSort,
} from '../../lib/circuits';
import { buildCircuitMapOverlay } from '../../lib/circuitMapOverlay';
import { formatLapTime } from '../../lib/lapFormat';
import { PAGE_ICONS } from '../../lib/pageIcons';
import { formatDistanceFromKm } from '../../lib/telemetryMeasurements';
import {
  fetchTrackContributionById,
  fetchGlobalTrackContributions,
  globalTrackContributionsListKey,
  trackContributionDetailKey,
} from '../../lib/trackContributions';

type UserLocation = { lat: number; lon: number };
type LocationStatus = 'locating' | 'idle' | 'available';
const ALL_TRACKS_PAGE_SIZE = 8;
const VISIBLE_CONFIGURATIONS = 2;

function requestUserLocation() {
  return new Promise<UserLocation>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is unavailable.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
      reject,
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 10 * 60 * 1000 },
    );
  });
}

function distanceLabel(distanceKm: number | null, preferences: MeasurementPreferences) {
  if (distanceKm === null) return null;
  const decimals = distanceKm < 10 ? 1 : 0;
  return `${formatDistanceFromKm(distanceKm, preferences, decimals)} away`;
}

function formatLeaderboardDate(recordedAtMs: number | null) {
  if (recordedAtMs === null) return 'Date unknown';
  return new Date(recordedAtMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function countryLabel(countryCode: string) {
  try {
    const name = new Intl.DisplayNames(undefined, { type: 'region' }).of(countryCode);
    return name ? `${name} (${countryCode})` : countryCode;
  } catch {
    return countryCode;
  }
}

function DrawerLeaderboardRow({ entry, rank, bestLapMs }: {
  entry: LeaderboardEntry;
  rank: number;
  bestLapMs: number;
}) {
  const gapMs = entry.lapTimeMs - bestLapMs;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '32px 92px minmax(0, 1fr)',
        gap: 1,
        alignItems: 'center',
        px: 1.25,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Typography color={rank <= 3 ? 'warning.main' : 'text.secondary'} fontWeight={900}>
        {rank}
      </Typography>
      <Box>
        <Typography fontWeight={900} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatLapTime(entry.lapTimeMs)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {rank === 1 ? 'Best lap' : `+${(gapMs / 1000).toFixed(3)}`}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={750} noWrap>
          {[entry.driverName, entry.kartLabel].filter(Boolean).join(' · ') || 'Team session'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {formatLeaderboardDate(entry.recordedAtMs)}
          {entry.lapNumber !== null ? ` · Lap ${entry.lapNumber}` : ''}
        </Typography>
      </Box>
    </Box>
  );
}

function CircuitDetailDrawer({ item, items, onSelect, onClose }: {
  item: CircuitBrowseItem | null;
  items: CircuitBrowseItem[];
  onSelect: (item: CircuitBrowseItem) => void;
  onClose: () => void;
}) {
  const measurementPreferences = useMeasurementPreferences();
  const circuit = item?.circuit ?? null;
  const contribution = circuit?.contribution ?? null;
  const leaderboard = item?.leaderboard ?? null;
  const { data: detailedContribution, isLoading: detailedContributionLoading } = useSWR(
    trackContributionDetailKey(contribution?.id),
    () => fetchTrackContributionById(contribution!.id),
    { revalidateOnFocus: false },
  );
  const configurations = useMemo(
    () => contribution
      ? items
        .filter((candidate) =>
          candidate.circuit.contribution?.trackId === contribution.trackId)
        .sort((left, right) =>
          (left.circuit.layoutName ?? '').localeCompare(right.circuit.layoutName ?? ''))
      : [],
    [contribution, items],
  );
  const activeContribution = detailedContribution && detailedContribution.id === contribution?.id
    ? detailedContribution
    : contribution;
  const detailedGeometry = detailedContribution && detailedContribution.id === contribution?.id
    ? detailedContribution.payload.detailedGeometry
    : undefined;
  const overlay = useMemo(
    () => activeContribution ? buildCircuitMapOverlay(activeContribution) : null,
    [activeContribution],
  );

  const mapViewport = useMemo(() => {
    const points = activeContribution?.payload.gpsPolyline ?? [];
    if (points.length === 0) return undefined;
    const center = points.reduce(
      (sum, point) => ({ lat: sum.lat + point.lat, lon: sum.lon + point.lon }),
      { lat: 0, lon: 0 },
    );
    return { lat: center.lat / points.length, lon: center.lon / points.length, zoom: 17 };
  }, [activeContribution]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(item)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640, lg: 720 },
          maxWidth: '100vw',
          bgcolor: 'background.default',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ flexShrink: 0, px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 900, lineHeight: 1.25 }} noWrap>
              {circuit?.name ?? 'Circuit'}
            </Typography>
            <Typography sx={{ mt: 0.3, fontSize: '0.72rem', color: 'text.secondary' }} noWrap>
              {circuit?.layoutName ?? 'Published circuit layout'}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close circuit details"
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.25, sm: 1.5 } }}>
          {circuit && overlay ? (
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Configuration</InputLabel>
                <Select
                  label="Configuration"
                  value={item?.circuit.id ?? ''}
                  onChange={(event) => {
                    const selected = configurations.find(
                      (candidate) => candidate.circuit.id === event.target.value,
                    );
                    if (selected) {
                      onSelect(selected);
                    }
                  }}
                >
                  {configurations.map((configuration) => (
                    <MenuItem
                      key={configuration.circuit.id}
                      value={configuration.circuit.id}
                    >
                      {configuration.circuit.layoutName || configuration.circuit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  height: { xs: 320, sm: 390 },
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {detailedGeometry ? (
                  <DetailedCircuitMap
                    geometry={detailedGeometry}
                    geometryKey={detailedContribution!.id}
                    layers={DETAILED_CIRCUIT_DEFAULT_LAYERS}
                    minHeight={0}
                  />
                ) : detailedContributionLoading && contribution?.payload.detailedGeometryVersion ? (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <GpsSpeedTrackMap
                    fill
                    points={overlay.referenceTrackGroups[0] ?? []}
                    showLapLines={false}
                    showLegend={false}
                    mapViewport={mapViewport}
                    mapMarkers={overlay.mapMarkers}
                    mapMarkerScale={1.2}
                    startFinishLine={overlay.startFinishLine}
                    fitBoundsPadding={44}
                    maxFitZoom={19}
                  />
                )}
              </Box>

              <SectionCard title="Circuit details" contentSx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {[
                    ['Direction', formatCircuitDirection(circuit.direction)],
                    ['Length', circuit.lapLengthM
                      ? formatDistanceFromKm(circuit.lapLengthM / 1000, measurementPreferences, 2)
                      : '—'],
                    ['Turns', circuit.cornerCount ?? '—'],
                    ['From you', item?.distanceKm !== null && item?.distanceKm !== undefined
                      ? formatDistanceFromKm(item.distanceKm, measurementPreferences, item.distanceKm < 10 ? 1 : 0)
                      : 'Location needed'],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={800}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
                {contribution?.notes?.trim() ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.5 }}>
                    {contribution.notes.trim()}
                  </Typography>
                ) : null}
              </SectionCard>

              <SectionCard
                title="Team leaderboard"
                subtitle={leaderboard
                  ? `${leaderboard.sessionCount} recorded session${leaderboard.sessionCount === 1 ? '' : 's'}`
                  : 'No recorded laps at this circuit yet'}
                action={<EmojiEventsOutlinedIcon sx={{ color: leaderboard ? 'warning.main' : 'text.secondary' }} />}
                contentSx={{ p: leaderboard ? 0 : 2 }}
              >
                {leaderboard ? (
                  <Stack spacing={0}>
                    {leaderboard.entries.map((entry, index) => (
                      <DrawerLeaderboardRow
                        key={`${entry.sessionKey}-${entry.lapNumber ?? 'best'}`}
                        entry={entry}
                        rank={index + 1}
                        bestLapMs={leaderboard.bestLapMs}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={0.5} sx={{ py: 2, textAlign: 'center' }}>
                    <Typography fontWeight={800}>No leaderboard times yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Times appear after your team records a session here.
                    </Typography>
                  </Stack>
                )}
              </SectionCard>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}

function CircuitTrackCard({
  group,
  onOpen,
  onFavorite,
}: {
  group: CircuitTrackGroup;
  onOpen: (item: CircuitBrowseItem) => void;
  onFavorite: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const measurementPreferences = useMeasurementPreferences();
  const [previewConfigurationId, setPreviewConfigurationId] = useState<string | null>(null);
  const [configurationsExpanded, setConfigurationsExpanded] = useState(false);
  const previewItem = group.configurations.find(
    (item) => item.circuit.id === previewConfigurationId,
  ) ?? group.configurations[0];
  const preview = previewItem?.circuit;
  if (!preview) return null;

  return (
    <Box onMouseLeave={() => setPreviewConfigurationId(null)} sx={{ height: '100%' }}>
      <SectionCard
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            borderColor: isDark ? 'rgba(96,165,250,0.42)' : 'rgba(124,58,237,0.28)',
            bgcolor: isDark ? 'rgba(96,165,250,0.035)' : 'rgba(124,58,237,0.03)',
            boxShadow: isDark ? 'none' : '0 8px 24px rgba(15,23,42,0.06)',
          },
        }}
      >
      <Stack spacing={1.1} sx={{ height: '100%' }}>
        <Box onClick={() => onOpen(previewItem!) } sx={{ cursor: 'pointer' }}>
          <CircuitTrackPreview key={preview.id} circuit={preview} height={120} />
        </Box>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={850} noWrap>{group.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {[group.locality, group.region, group.countryCode].filter(Boolean).join(', ')
                || `${group.configurations.length} configuration${group.configurations.length === 1 ? '' : 's'}`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={group.favorite ? `Remove ${group.name} from favorites` : `Favorite ${group.name}`}
            onClick={onFavorite}
          >
            {group.favorite
              ? <FavoriteIcon sx={{ fontSize: 19, color: 'error.main' }} />
              : <FavoriteBorderIcon sx={{ fontSize: 19 }} />}
          </IconButton>
        </Stack>
        <Stack spacing={0.35}>
          {(configurationsExpanded ? group.configurations : group.configurations.slice(0, VISIBLE_CONFIGURATIONS)).map((item) => {
            const circuit = item.circuit;
            const length = circuit.lapLengthM
              ? formatDistanceFromKm(circuit.lapLengthM / 1000, measurementPreferences, 2)
              : null;
            return (
              <Button
                key={circuit.id}
                onClick={() => onOpen(item)}
                onMouseEnter={() => setPreviewConfigurationId(circuit.id)}
                onFocus={() => setPreviewConfigurationId(circuit.id)}
                endIcon={<ChevronRightIcon />}
                sx={{
                  justifyContent: 'space-between',
                  px: 0.75,
                  py: 0.45,
                  minHeight: 0,
                  textTransform: 'none',
                  color: 'text.primary',
                  fontSize: '0.75rem',
                }}
              >
                <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                  <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 700 }} noWrap>
                    {circuit.layoutName || 'Default configuration'}
                  </Typography>
                  <Typography component="span" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }} noWrap>
                    {[
                      length,
                      circuit.direction ? formatCircuitDirection(circuit.direction) : null,
                      circuit.cornerCount != null ? `${circuit.cornerCount} turns` : null,
                      item.leaderboard ? `${item.leaderboard.sessionCount} sessions` : null,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Button>
            );
          })}
          {group.configurations.length > VISIBLE_CONFIGURATIONS ? (
            <Button
              size="small"
              onClick={() => setConfigurationsExpanded((expanded) => !expanded)}
              sx={{ alignSelf: 'flex-start', px: 0.75, minWidth: 0, textTransform: 'none', fontSize: '0.68rem' }}
            >
              {configurationsExpanded
                ? 'Show fewer'
                : `View all (${group.configurations.length})`}
            </Button>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
          {group.sessionCount > 0 ? (
            <Chip size="small" icon={<EmojiEventsOutlinedIcon />} label={`${group.sessionCount} sessions`} variant="outlined" />
          ) : null}
          {distanceLabel(group.distanceKm, measurementPreferences) ? (
            <Chip size="small" icon={<LocationOnOutlinedIcon />} label={distanceLabel(group.distanceKm, measurementPreferences)!} variant="outlined" />
          ) : null}
        </Stack>
      </Stack>
      </SectionCard>
    </Box>
  );
}

function CircuitTrackSection({
  title,
  subtitle,
  groups,
  onOpen,
  onFavorite,
}: {
  title: string;
  subtitle: string;
  groups: CircuitTrackGroup[];
  onOpen: (item: CircuitBrowseItem) => void;
  onFavorite: (group: CircuitTrackGroup) => void;
}) {
  if (groups.length === 0) return null;

  return (
    <Box component="section">
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={850}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        </Box>
      </Stack>
      <Grid container spacing={1.5}>
        {groups.map((group) => (
          <Grid item key={group.trackId} xs={12} sm={6} lg={4} xl={3}>
            <CircuitTrackCard
              group={group}
              onOpen={onOpen}
              onFavorite={() => onFavorite(group)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export function CircuitsPage() {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>('locating');
  const [selectedCircuit, setSelectedCircuit] = useState<CircuitBrowseItem | null>(null);
  const [query, setQuery] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [region, setRegion] = useState('');
  const [sort, setSort] = useState<CircuitTrackSort>('top');
  const [visibleCount, setVisibleCount] = useState(ALL_TRACKS_PAGE_SIZE);

  const {
    data: contributions,
    error: contributionsError,
    isLoading: contributionsLoading,
    isValidating: contributionsValidating,
    mutate: retryContributions,
  } = useSWR(
    globalTrackContributionsListKey('summary'),
    () => fetchGlobalTrackContributions('summary'),
    { revalidateOnMount: true, revalidateOnFocus: false, dedupingInterval: 0 },
  );
  const {
    data: tracks,
    error: tracksError,
    isLoading: tracksLoading,
    isValidating: tracksValidating,
    mutate: retryTracks,
  } = useSWR(
    telemetryTracksKey,
    fetchTelemetryTracks,
    { revalidateOnMount: true, revalidateOnFocus: false, dedupingInterval: 0 },
  );
  const { data: leaderboard = [] } = useSWR(
    contributions ? leaderboardKey : null,
    fetchLeaderboard,
    { revalidateOnFocus: false },
  );
  const { data: preferences, mutate: mutatePreferences } = useSWR(
    contributions ? circuitPreferencesKey : null,
    fetchCircuitPreferences,
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    let active = true;
    requestUserLocation()
      .then((nextLocation) => {
        if (!active) return;
        setLocation(nextLocation);
        setLocationStatus('available');
      })
      .catch(() => {
        if (active) setLocationStatus('idle');
      });

    return () => {
      active = false;
    };
  }, []);

  const trackById = useMemo(
    () => new Map((tracks ?? []).map((track) => [track.id, track])),
    [tracks],
  );
  const circuits = useMemo(
    () => buildCircuitCatalogItems([], contributions, trackById),
    [contributions, trackById],
  );
  const groups = useMemo(
    () => buildCircuitTrackGroups(
      circuits,
      leaderboard,
      location,
      preferences?.favoriteTrackIds ?? [],
    ),
    [circuits, leaderboard, location, preferences?.favoriteTrackIds],
  );
  const filteredGroups = useMemo(
    () => filterAndSortCircuitTrackGroups(groups, { query, countryCode, region, sort }),
    [countryCode, groups, query, region, sort],
  );
  const topGroups = useMemo(
    () => selectMostUsedCircuitTrackGroups(groups),
    [groups],
  );
  const countries = useMemo(
    () => [...new Set(groups.map((group) => group.countryCode).filter((value): value is string => Boolean(value)))]
      .sort(),
    [groups],
  );
  const regions = useMemo(
    () => [...new Set(groups
      .filter((group) => !countryCode || group.countryCode === countryCode)
      .map((group) => group.region)
      .filter((value): value is string => Boolean(value)))]
      .sort(),
    [countryCode, groups],
  );
  const showTopSection = !query && !countryCode && !region && sort === 'top';
  const listedGroups = useMemo(() => {
    if (!showTopSection) {
      return filteredGroups;
    }

    const topTrackIds = new Set(topGroups.map((group) => group.trackId));
    const remaining = groups.filter((group) => !topTrackIds.has(group.trackId));
    return sortCircuitTrackGroupsByDistance(remaining);
  }, [filteredGroups, groups, showTopSection, topGroups]);
  const visibleGroups = listedGroups.slice(0, visibleCount);
  const allBrowseItems = useMemo(
    () => groups.flatMap((group) => group.configurations),
    [groups],
  );
  const hasRemoteCatalog = Boolean(contributions?.length || tracks?.length);
  const loading = !hasRemoteCatalog && (contributionsLoading || tracksLoading);
  const catalogError = !hasRemoteCatalog && (contributionsError || tracksError);

  useEffect(() => {
    setVisibleCount(ALL_TRACKS_PAGE_SIZE);
  }, [query, countryCode, region, sort]);

  function openCircuit(item: CircuitBrowseItem) {
    setSelectedCircuit(item);
  }

  async function toggleFavorite(group: CircuitTrackGroup) {
    const current = new Set(preferences?.favoriteTrackIds ?? []);
    if (current.has(group.trackId)) current.delete(group.trackId);
    else current.add(group.trackId);
    const favoriteTrackIds = [...current];
    await mutatePreferences(
      updateCircuitPreferences({ favoriteTrackIds }),
      { optimisticData: { favoriteTrackIds, updatedAt: new Date().toISOString() }, rollbackOnError: true },
    );
  }

  async function locateCircuits() {
    setLocationStatus('locating');
    try {
      const nextLocation = await requestUserLocation();
      setLocation(nextLocation);
      setLocationStatus('available');
    } catch {
      setLocationStatus('idle');
    }
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', minWidth: 0 }}>
      <PageHeader
        title="Circuits"
        subtitle="Tracks you visit most, plus the full catalog sorted by distance."
        icon={PAGE_ICONS.circuits}
        mb={2}
        actions={(
          location ? (
            <Chip icon={<LocationOnOutlinedIcon />} label="Nearest first" variant="outlined" sx={{ height: 28, fontWeight: 600 }} />
          ) : (
            <Button
              variant="outlined"
              startIcon={locationStatus === 'locating'
                ? <CircularProgress size={16} color="inherit" />
                : <LocationOnOutlinedIcon />}
              disabled={locationStatus === 'locating'}
              onClick={() => void locateCircuits()}
            >
              {locationStatus === 'locating' ? 'Locating…' : 'Sort by distance'}
            </Button>
          )
        )}
      />

      <SectionCard sx={{ mb: 2 }} contentSx={{ p: 1.5 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search tracks, configurations, state, or country"
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 19, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  value={countryCode}
                  onChange={(event) => {
                    setCountryCode(event.target.value);
                    setRegion('');
                  }}
                >
                  <MenuItem value="">All countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>{countryLabel(country)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>State / province</InputLabel>
                <Select label="State / province" value={region} onChange={(event) => setRegion(event.target.value)}>
                  <MenuItem value="">All regions</MenuItem>
                  {regions.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort</InputLabel>
                <Select label="Sort" value={sort} onChange={(event) => setSort(event.target.value as CircuitTrackSort)}>
                  <MenuItem value="top">Top</MenuItem>
                  <MenuItem value="used">Most used</MenuItem>
                  <MenuItem value="recent">Recent</MenuItem>
                  <MenuItem value="nearest">Nearest</MenuItem>
                  <MenuItem value="name">A–Z</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </SectionCard>

      {contributionsValidating || tracksValidating ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress aria-label="Loading circuit catalog" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Loading tracks and configurations…
          </Typography>
        </Box>
      ) : null}

      {loading ? (
        <CardGridSkeleton count={6} height={260} />
      ) : catalogError ? (
        <Alert
          severity="error"
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => void Promise.all([retryContributions(), retryTracks()])}
            >
              Retry
            </Button>
          )}
        >
          Could not load the circuit catalog. Check the API connection and try again.
        </Alert>
      ) : groups.length === 0 ? (
        <SectionCard>
          <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: 'center' }}>
            <PublicOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography fontWeight={800}>No published circuits yet</Typography>
          </Stack>
        </SectionCard>
      ) : (
        <Stack spacing={3}>
          {showTopSection && topGroups.length > 0 ? (
            <CircuitTrackSection
              title="Top tracks"
              subtitle="Tracks your team visits most often."
              groups={topGroups}
              onOpen={openCircuit}
              onFavorite={(group) => void toggleFavorite(group)}
            />
          ) : null}
          <CircuitTrackSection
            title="All tracks"
            subtitle={showTopSection
              ? (location
                ? `${listedGroups.length} tracks · sorted by distance from you`
                : `${listedGroups.length} tracks · allow location to sort by distance`)
              : `${listedGroups.length} matching tracks · configurations listed within each track`}
            groups={visibleGroups}
            onOpen={openCircuit}
            onFavorite={(group) => void toggleFavorite(group)}
          />
          {visibleCount < listedGroups.length ? (
            <Button
              variant="outlined"
              onClick={() => setVisibleCount((count) => count + ALL_TRACKS_PAGE_SIZE)}
              sx={{ alignSelf: 'center' }}
            >
              Load {ALL_TRACKS_PAGE_SIZE} more tracks
            </Button>
          ) : null}
        </Stack>
      )}

      <CircuitDetailDrawer
        item={selectedCircuit}
        items={allBrowseItems}
        onSelect={setSelectedCircuit}
        onClose={() => setSelectedCircuit(null)}
      />
    </Box>
  );
}
